import { UserSelect } from "@app/filter/UserSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Dashboard, ShareDashboardRequest } from "@dto/Dashboard";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import User, { DashboardRightType, FullRightAccess, NoneRightAccess, ReadRightAccess, Right, RightAccess, Role } from "@dto/Users";
import { BASE_URL } from "@utils/constants";
import { fetchRoles } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_ViewSharingEditor, TW_ViewSharingEditorNewFilter, TW_ViewSharingEditorNoRoles, TW_ViewSharingEditorOwnerSection, TW_ViewSharingEditorOwnerSectionTitle, TW_ViewSharingEditorRemoveFilter, TW_ViewSharingEditorRemoveFilterDisabled, TW_ViewSharingEditorRoleRightSelect, TW_ViewSharingEditorRoleSelect, TW_ViewSharingEditorSection, TW_ViewSharingEditorSectionTitle, TW_ViewSharingEditorUserRightSelect, useIconHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useAppStore, useModelStore } from "src/store";

interface DashboardSharingEditorProps {
  dashboard: Dashboard;
  onClose: () => void;
  onDashboardUpdate: (dashboard: Dashboard) => void;
}

const dashboardRightAccesses = [FullRightAccess, ReadRightAccess, NoneRightAccess];

function DashboardSharingEditor({ dashboard, onClose: _onClose, onDashboardUpdate }: DashboardSharingEditorProps) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [usedRoleKeys, setUsedRoleKeys] = useState<string[]>([]);
  const [rightPerRole, setRightPerRole] = useState<Map<string, Right | null>>(new Map());
  const [userSharesByType, setUserSharesByType] = useState<Map<RightAccess, User[]>>(new Map());
  const [usedUserRightAccess, setUsedUserRightAccess] = useState<RightAccess[]>([]);
  const workspace = useModelStore((state) => state.selectedWorkspace);
  const dashboards = useModelStore((state) => state.dashboards);
  const updateDashboards = useModelStore((state) => state.updateDashboards);
  const selectedDashboard = useModelStore((state) => state.selectedDashboard);
  const updateSelectedDashboard = useModelStore((state) => state.updateSelectedDashboard);
  const users = useAppStore((state) => state.usersByKeys);
  const fetchUsers = useAppStore((state) => state.fetchUsers);
  const { t } = useTranslation();

  useEffect(() => {
    const nextUserSharesByType = new Map<RightAccess, User[]>();
    Array.from(users.values()).forEach((user) => {
      const rights = (user.rights ?? []).filter((right) => right.type === DashboardRightType && right.recordKey === dashboard._key);
      rights.forEach((right) => {
        nextUserSharesByType.set(right.access, [...(nextUserSharesByType.get(right.access) ?? []), user]);
      });
    });
    const usedRights = Array.from(nextUserSharesByType.keys());
    setUsedUserRightAccess(dashboardRightAccesses.filter((right) => usedRights.includes(right)));
    setUserSharesByType(nextUserSharesByType);
  }, [dashboard, users]);

  function onRoleFetched(fetchedRoles: Role[]) {
    const nextRightPerRole = new Map<string, Right | null>();
    const nextUsedRoleKeys: string[] = [];
    fetchedRoles.forEach((role) => {
      const right = role.rights.find((currentRight) => currentRight.type === DashboardRightType && currentRight.recordKey === dashboard._key) ?? null;
      nextRightPerRole.set(role._key, right);
      if (right) {
        nextUsedRoleKeys.push(role._key);
      }
    });
    setRightPerRole(nextRightPerRole);
    setRoles(fetchedRoles);
    setUsedRoleKeys(nextUsedRoleKeys);
  }

  useEffect(() => {
    fetchRoles(workspace?._key, null, null, onRoleFetched);
  }, [workspace, dashboard]);

  function computeShared(nextRightPerRole: Map<string, Right | null>, nextUserSharesByType: Map<RightAccess, User[]>) {
    const hasRoleShare = Array.from(nextRightPerRole.values()).some((right) => right && right.active && right.access !== NoneRightAccess);
    const hasUserShare = Array.from(nextUserSharesByType.entries()).some(([access, sharedUsers]) => access !== NoneRightAccess && sharedUsers.length > 0);
    return hasRoleShare || hasUserShare || !dashboard.ownerKey;
  }

  function applyDashboardUpdate(shared: boolean) {
    const newDashboard = {
      ...dashboard,
      shared,
    };
    const newDashboards = (dashboards ?? []).map((currentDashboard) => currentDashboard._key === dashboard._key ? newDashboard : currentDashboard);
    updateDashboards(newDashboards);
    if (selectedDashboard?._key === dashboard._key) {
      updateSelectedDashboard(newDashboard);
    }
    onDashboardUpdate(newDashboard);
  }

  const onRoleChange = (roleKey: string, newRole: Role) => {
    const newRight = newRole.rights.find((right) => right.type === DashboardRightType && right.recordKey === dashboard._key) ?? null;
    const shareDashboardRequest: ShareDashboardRequest = {
      dashboardKey: dashboard._key,
      roleKey,
      right: newRight,
    };

    put<Role>(`${BASE_URL}/model/workspaces/${workspace?._key}/dashboards/${dashboard._key}/share`, shareDashboardRequest).then((response) => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        return;
      }

      const role = response as Role;
      const nextRoles = roles.map((currentRole) => currentRole._key === roleKey ? role : currentRole);
      const nextRightPerRole = new Map<string, Right | null>(rightPerRole);
      const nextDashboardRight = newRole.rights.find((currentRight) => currentRight.type === DashboardRightType && currentRight.recordKey === dashboard._key) ?? null;
      if (nextDashboardRight) {
        nextRightPerRole.set(role._key, nextDashboardRight);
      } else {
        nextRightPerRole.delete(roleKey);
      }

      let nextUsedRoleKeys = [...usedRoleKeys];
      if (usedRoleKeys.includes(roleKey) && !nextDashboardRight) {
        nextUsedRoleKeys = nextUsedRoleKeys.filter((key) => key !== roleKey);
      } else if (!usedRoleKeys.includes(roleKey) && nextDashboardRight) {
        nextUsedRoleKeys.push(roleKey);
      }

      applyDashboardUpdate(computeShared(nextRightPerRole, userSharesByType));
      setRoles(nextRoles);
      setRightPerRole(nextRightPerRole);
      setUsedRoleKeys(nextUsedRoleKeys);
    });
  };

  const updateUserRights = (user: User, newRight: Right, onUpdate: () => void) => {
    const shareDashboardRequest: ShareDashboardRequest = {
      dashboardKey: dashboard._key,
      userKey: user._key,
      right: newRight,
    };

    put<Role>(`${BASE_URL}/model/workspaces/${workspace?._key}/dashboards/${dashboard._key}/share`, shareDashboardRequest).then((response) => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        return;
      }

      fetchUsers();
      onUpdate();
    });
  };

  const onUserChange = (rightAccess: RightAccess, oldUsers: User[], newUsers: User[]) => {
    const oldUserKeys = oldUsers.map((user) => user._key);
    const newUserKeys = newUsers.map((user) => user._key);
    const usersToAdd = newUsers.filter((user) => !oldUserKeys.includes(user._key));
    const usersToRemove = oldUsers.filter((user) => !newUserKeys.includes(user._key));

    usersToAdd.forEach((user) => {
      const newRight: Right = {
        type: DashboardRightType,
        access: rightAccess,
        recordKey: dashboard._key,
        active: true,
      };
      updateUserRights(user, newRight, () => {
        const nextUserSharesByType = new Map<RightAccess, User[]>(userSharesByType);
        nextUserSharesByType.set(rightAccess, [...(nextUserSharesByType.get(rightAccess) ?? []), user]);
        applyDashboardUpdate(computeShared(rightPerRole, nextUserSharesByType));
      });
    });

    usersToRemove.forEach((user) => {
      const newRight: Right = {
        type: DashboardRightType,
        access: NoneRightAccess,
        recordKey: dashboard._key,
        active: true,
      };
      updateUserRights(user, newRight, () => {
        const nextUserSharesByType = new Map<RightAccess, User[]>(userSharesByType);
        nextUserSharesByType.set(rightAccess, (nextUserSharesByType.get(rightAccess) ?? []).filter((sharedUser) => sharedUser._key !== user._key));
        applyDashboardUpdate(computeShared(rightPerRole, nextUserSharesByType));
      });
    });
  };

  const onRemoveRightAccess = (rightAccess: RightAccess, oldUsers: User[]) => {
    oldUsers.forEach((user) => {
      const newRight: Right = {
        type: DashboardRightType,
        access: NoneRightAccess,
        recordKey: dashboard._key,
        active: true,
      };
      updateUserRights(user, newRight, () => {
        const nextUserSharesByType = new Map<RightAccess, User[]>(userSharesByType);
        nextUserSharesByType.set(rightAccess, []);
        applyDashboardUpdate(computeShared(rightPerRole, nextUserSharesByType));
      });
    });
  };

  const roleItemKeyPrefix = dashboard._key + "-sharing-role-";
  const userItemKeyPrefix = dashboard._key + "-sharing-user-";
  const ownerUser = users.get(dashboard.ownerKey ?? "");

  return (
    <div className={TW_ViewSharingEditor}>
      {ownerUser && (
        <div className={TW_ViewSharingEditorOwnerSection}>
          <p className={TW_ViewSharingEditorOwnerSectionTitle}>
            {translate(t, "dashboard.sharing.owner", [ownerUser.firstname, ownerUser.lastname, ownerUser.username ?? ""])}
          </p>
        </div>
      )}
      <div className={TW_ViewSharingEditorSection}>
        <p className={TW_ViewSharingEditorSectionTitle}>{t("dashboard.sharing.roles")}</p>
        {roles.length === 0 && <p className={TW_ViewSharingEditorNoRoles}>{t("dashboard.sharing.no.defined.roles")}</p>}
        {roles.filter((role) => rightPerRole.get(role._key)).map((role, index) => (
          <RoleDashboardSharingItem
            key={roleItemKeyPrefix + index}
            dashboard={dashboard}
            role={role}
            right={rightPerRole.get(role._key) ?? null}
            onRoleChange={onRoleChange}
            roles={roles}
            usedRoleKeys={usedRoleKeys}
          />
        ))}
        {usedRoleKeys.length < roles.length && (
          <RoleDashboardSharingItem
            key={roleItemKeyPrefix + usedRoleKeys.length}
            dashboard={dashboard}
            role={null}
            right={null}
            onRoleChange={onRoleChange}
            roles={roles}
            usedRoleKeys={usedRoleKeys}
          />
        )}
      </div>
      <div className={TW_ViewSharingEditorSection}>
        <p className={TW_ViewSharingEditorSectionTitle}>{t("dashboard.sharing.users")}</p>
        {usedUserRightAccess.filter((right) => userSharesByType.has(right) && userSharesByType.get(right)!.length > 0).map((right, index) => {
          const sharedUsers = userSharesByType.get(right);
          return (
            <UserDashboardSharingItem
              key={userItemKeyPrefix + "-" + usedUserRightAccess.length + right + "-" + index}
              right={right}
              users={sharedUsers!}
              onUserChange={onUserChange}
              onRemoveRight={onRemoveRightAccess}
              usedRightAccess={usedUserRightAccess}
            />
          );
        })}
        {usedUserRightAccess.length < dashboardRightAccesses.length && (
          <UserDashboardSharingItem
            key={userItemKeyPrefix + usedUserRightAccess.length}
            right={null}
            users={[]}
            onUserChange={onUserChange}
            onRemoveRight={onRemoveRightAccess}
            usedRightAccess={usedUserRightAccess}
          />
        )}
      </div>
    </div>
  );
}

interface RoleDashboardSharingItemProps {
  dashboard: Dashboard;
  role: Role | null;
  right: Right | null;
  onRoleChange: (roleKey: string, newRole: Role) => void;
  roles: Role[];
  usedRoleKeys: string[];
}

function RoleDashboardSharingItem({ dashboard, role, right, onRoleChange, roles, usedRoleKeys }: RoleDashboardSharingItemProps) {
  const { t } = useTranslation();
  const [selectedRoleKey, setSelectedRoleKey] = useState<string | undefined>(undefined);
  const wsColor = useModelStore((state) => state.wsColor);
  const trashHover = useIconHover("#00000088", wsColor);

  useEffect(() => {
    if (role) {
      setSelectedRoleKey(role._key);
    }
  }, [role, right]);

  const onRoleSelectionChange = (roleKey: string) => {
    if (roleKey !== selectedRoleKey) {
      setSelectedRoleKey(roleKey);
    }
  };

  const onRightAccessChange = (newRightAccess: string) => {
    if (!selectedRoleKey) {
      return;
    }
    const foundRole = roles.find((currentRole) => currentRole._key === selectedRoleKey);
    if (!foundRole || right?.access === newRightAccess) {
      return;
    }

    const newRight: Right = right ? {
      ...right,
      access: newRightAccess as RightAccess,
    } : {
      type: DashboardRightType,
      access: newRightAccess as RightAccess,
      recordKey: dashboard._key,
      active: true,
    };
    const newRights = foundRole.rights.filter((currentRight) => !(currentRight.type === DashboardRightType && currentRight.recordKey === dashboard._key));
    if (newRight.access !== NoneRightAccess) {
      newRights.push(newRight);
    }
    onRoleChange(foundRole._key, {
      ...foundRole,
      rights: newRights,
    });
  };

  const onRemoveRight = () => {
    if (!selectedRoleKey) {
      return;
    }
    const roleToRemove = roles.find((currentRole) => currentRole._key === selectedRoleKey);
    if (!roleToRemove) {
      return;
    }
    setSelectedRoleKey(undefined);
    onRoleChange(roleToRemove._key, {
      ...roleToRemove,
      rights: roleToRemove.rights.filter((currentRight) => !(currentRight.type === DashboardRightType && currentRight.recordKey === dashboard._key)),
    });
  };

  const disabledTrash = !selectedRoleKey;

  return (
    <div className={TW_ViewSharingEditorNewFilter}>
      <Select key={selectedRoleKey ?? "undefined"} onValueChange={onRoleSelectionChange} value={selectedRoleKey} disabled={selectedRoleKey !== undefined}>
        <SelectTrigger className={TW_ViewSharingEditorRoleSelect}>
          <SelectValue placeholder={t("dashboard.sharing.role.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {roles.map((role) => (
            <SelectItem key={role._key} value={role._key} disabled={usedRoleKeys.includes(role._key)}>
              {role.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={onRightAccessChange} value={right?.access}>
        <SelectTrigger className={TW_ViewSharingEditorRoleRightSelect}>
          <SelectValue placeholder={t("dashboard.sharing.right.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {dashboardRightAccesses.map((accessRight) => (
            <SelectItem key={accessRight} value={accessRight}>
              {t(`right.access.value.dashboard.${accessRight}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilterDisabled} />}
      {!disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilter} onClick={onRemoveRight} {...trashHover} />}
    </div>
  );
}

interface UserDashboardSharingItemProps {
  right: RightAccess | null;
  users: User[];
  onUserChange: (rightAccess: RightAccess, oldUsers: User[], newUsers: User[]) => void;
  onRemoveRight: (rightAccess: RightAccess, oldUsers: User[]) => void;
  usedRightAccess: RightAccess[];
}

function UserDashboardSharingItem({ right, users, onUserChange, onRemoveRight, usedRightAccess }: UserDashboardSharingItemProps) {
  const { t } = useTranslation();
  const wsColor = useModelStore((state) => state.wsColor);
  const trashHover = useIconHover("#00000088", wsColor);
  const allUsers = Array.from(useAppStore((state) => state.usersByKeys).values());
  const [selectedRightAccess, setSelectedRightAccess] = useState<RightAccess | undefined>(right ?? undefined);

  const onRightAccessSelectionChange = (newRightAccess: string) => {
    if (newRightAccess !== selectedRightAccess) {
      setSelectedRightAccess(newRightAccess as RightAccess);
    }
  };

  const onUserSelectionChange = (selectedUsers: User[], _currentUserSelected: boolean) => {
    if (!selectedRightAccess) {
      return;
    }
    onUserChange(selectedRightAccess, users, selectedUsers);
  };

  const onRemoveRightAccess = () => {
    if (!selectedRightAccess) {
      return;
    }
    setSelectedRightAccess(undefined);
    onRemoveRight(selectedRightAccess, users);
  };

  const disabledTrash = !selectedRightAccess;
  const disabledUsers = !selectedRightAccess;

  return (
    <div className={TW_ViewSharingEditorNewFilter}>
      <Select key={selectedRightAccess ?? "undefined"} onValueChange={onRightAccessSelectionChange} value={selectedRightAccess} disabled={selectedRightAccess !== undefined}>
        <SelectTrigger className={TW_ViewSharingEditorUserRightSelect}>
          <SelectValue placeholder={t("dashboard.sharing.right.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {dashboardRightAccesses.map((accessRight) => (
            <SelectItem key={accessRight} value={accessRight} disabled={usedRightAccess.includes(accessRight)}>
              {t(`right.access.value.dashboard.${accessRight}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-row justify-start items-center gap-2 w-full">
        <UserSelect
          allUsers={allUsers}
          selectedUsers={users}
          disabled={disabledUsers}
          currentUserSelected={false}
          onSelectionChange={onUserSelectionChange}
          className="w-full"
          allowCurrentUserSelection={false}
        />
      </div>
      {disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilterDisabled} />}
      {!disabledTrash && <Trash2 className={TW_ViewSharingEditorRemoveFilter} onClick={onRemoveRightAccess} {...trashHover} />}
    </div>
  );
}

export default DashboardSharingEditor;
